import { z } from 'zod';
import { PRODUCT_STATUSES } from '../types/enums.js';
import { moneySchema, optionalMoneySchema, paginationQuerySchema, slugSchema, sortOrderSchema, uuidSchema, } from './common.js';
export const productVariantInputSchema = z.object({
    id: uuidSchema.optional(),
    name: z.string().trim().min(1, 'Variant name is required').max(120),
    sku: z.string().trim().min(1).max(64).nullable().optional(),
    price: moneySchema,
    compareAtPrice: optionalMoneySchema,
    cost: optionalMoneySchema,
    stock: z.number().int().min(0).default(0),
    weightGrams: z.number().int().min(0).nullable().optional(),
    /** Free-form attribute map, e.g. { "Size": "M", "Colour": "Black" }. */
    attributes: z.record(z.string().max(60), z.string().max(120)).default({}),
    position: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
});
export const productImageInputSchema = z.object({
    id: uuidSchema.optional(),
    url: z.string().url('Enter a valid image URL').max(2048),
    alt: z.string().trim().max(200).default(''),
    position: z.number().int().min(0).default(0),
});
export const createProductSchema = z.object({
    name: z.string().trim().min(2, 'Product name is required').max(200),
    /** Blank means "generate it from the name", as the form's hint promises. */
    slug: z
        .union([
        z
            .string()
            .trim()
            .max(0)
            .transform(() => undefined),
        slugSchema,
    ])
        .optional(),
    description: z.string().trim().max(20_000).nullable().optional(),
    shortDescription: z.string().trim().max(500).nullable().optional(),
    /** Blank means "generate one" — kept as null so it is not stored as ''. */
    sku: z
        .union([
        z
            .string()
            .trim()
            .max(0)
            .transform(() => null),
        z.string().trim().max(64),
    ])
        .nullable()
        .optional(),
    price: moneySchema,
    compareAtPrice: optionalMoneySchema,
    cost: optionalMoneySchema,
    stock: z.number().int().min(0).default(0),
    /** When false the product can be bought regardless of the stock counter. */
    trackInventory: z.boolean().default(true),
    weightGrams: z.number().int().min(0).nullable().optional(),
    status: z.enum(PRODUCT_STATUSES).default('DRAFT'),
    /** '' is the form's "No category" choice — stored as none, not rejected. */
    categoryId: z
        .union([
        z
            .string()
            .trim()
            .max(0)
            .transform(() => null),
        uuidSchema,
    ])
        .nullable()
        .optional(),
    isDigital: z.boolean().default(false),
    images: z.array(productImageInputSchema).max(12).default([]),
    variants: z.array(productVariantInputSchema).max(50).default([]),
    metadata: z.record(z.string(), z.unknown()).default({}),
});
/** All fields optional on update; at least one must be present. */
export const updateProductSchema = createProductSchema.partial().refine((value) => Object.keys(value).length > 0, { message: 'Provide at least one field to update' });
export const productListQuerySchema = paginationQuerySchema.extend({
    search: z.string().trim().max(200).optional(),
    status: z.enum(PRODUCT_STATUSES).optional(),
    categoryId: uuidSchema.optional(),
    sortBy: z.enum(['createdAt', 'updatedAt', 'name', 'price', 'stock']).default('createdAt'),
    sortOrder: sortOrderSchema,
});
export const createCategorySchema = z.object({
    name: z.string().trim().min(2, 'Category name is required').max(120),
    slug: slugSchema.optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    parentId: uuidSchema.nullable().optional(),
    position: z.number().int().min(0).default(0),
});
export const updateCategorySchema = createCategorySchema.partial();
/** Manual stock adjustment from the product screen. */
export const adjustStockSchema = z.object({
    variantId: uuidSchema.nullable().optional(),
    /** Signed delta: -3 removes three units. */
    delta: z.number().int().refine((v) => v !== 0, 'Enter a non-zero adjustment'),
    reason: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=product.js.map